import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  create,
  type InternalAxiosRequestConfig,
} from 'axios';
import { toast } from 'sonner';
import { BASE_URL } from '@avanta/shared/constants/common';
import { ROUTES } from '@avanta/shared/constants/routes';
import { t } from '@avanta/shared/i18n';
import { useAuthStore } from '@avanta/shared/stores/auth';
import { type AuthInfo } from '@avanta/shared/types/auth';
import { getRefreshToken } from '@avanta/shared/utils/storage';
import { refreshAccessToken } from '../auth';
import { ErrorService, type ApiErrorData } from '../error';

export interface ServiceRequestConfig<Data = unknown> extends AxiosRequestConfig<Data> {
  skipGlobalErrorHandler?: boolean;
}

type InternalServiceRequestConfig = InternalAxiosRequestConfig &
  Pick<ServiceRequestConfig, 'skipGlobalErrorHandler'>;

export const service = create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'es',
  },
});

const _handleRequest = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  const token = useAuthStore.getState().token();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

const _handleSuccess = async (response: AxiosResponse): Promise<AxiosResponse> => {
  if (response.status === 202 || response.status === 204) {
    return { ...response, data: null };
  }
  return response;
};

const _handleTokenRefresh = async (refreshToken: string): Promise<string | null> => {
  try {
    return await refreshAccessToken(refreshToken);
  } catch {
    toast.error(t('Common.refreshToken.error'));
    return null;
  }
};

const _updateAuthInfo = (newAccessToken: string): void => {
  const currentAuthInfo = useAuthStore.getState().authInfo as AuthInfo;
  useAuthStore.getState().setAuthInfo({
    ...currentAuthInfo,
    access_token: newAccessToken,
  });
};

const _handleError =
  () =>
  async (error: AxiosError<ApiErrorData>): Promise<never> => {
    const originalRequest = error.config as InternalServiceRequestConfig | undefined;

    if (!originalRequest || !error.response) {
      return Promise.reject(error);
    }

    if (originalRequest.skipGlobalErrorHandler) {
      return Promise.reject(error);
    }

    if (error.response.status === 401) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        window.location.href = ROUTES.LOGIN;
        return Promise.reject(error);
      }

      const newAccessToken = await _handleTokenRefresh(refreshToken);

      if (newAccessToken) {
        _updateAuthInfo(newAccessToken);
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return axios(originalRequest);
      }

      window.location.href = ROUTES.LOGIN;
    }

    ErrorService.handlerErrorRequest(error);

    return Promise.reject(error);
  };

service.interceptors.request.use(_handleRequest);
service.interceptors.response.use(_handleSuccess, _handleError());
